import { useDebouncedValue } from '@mantine/hooks'
import { useState } from 'react'

export function useDebouncedFilter<T>({
  data,
  defaultFilterField,
  searchDebounceMs = 300,
}: {
  data: T[]
  defaultFilterField?: keyof T
  searchDebounceMs?: number
}) {
  const [filterField, setFilterField] = useState<keyof T | undefined>(defaultFilterField)
  const [search, setSearch] = useState('')

  const [effectiveSearchTerm] = useDebouncedValue(search, searchDebounceMs)

  const handleFilterFieldChange = (field: keyof T | undefined) => {
    setFilterField(field)
    setSearch('')
  }

  const handleSearchChange = (search: string) => {
    setSearch(search)
  }

  // Debounced filtering based on effective search term
  const filteredData = !effectiveSearchTerm
    ? data
    : data.filter(item => {
        if (filterField && effectiveSearchTerm) {
          const fieldValue = item[filterField]

          if (fieldValue === null || fieldValue === undefined) return false

          const normalizeString = (str: string) =>
            str
              .toLocaleLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')

          const searchNormalized = normalizeString(effectiveSearchTerm)
          const fieldNormalized = normalizeString(fieldValue.toString())

          return fieldNormalized.includes(searchNormalized)
        }
        return true
      })

  return {
    // State
    filterField,
    search,
    filteredData,
    isDebouncing: searchDebounceMs ? search !== effectiveSearchTerm : false,

    // Actions
    setFilterField,
    setSearch,

    // Handlers
    handleFilterFieldChange,
    handleSearchChange,
  }
}
